import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Trash2, ImageIcon, AlertCircle, Loader2 } from 'lucide-react';
import type { UploadedImage } from '../types';
import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE_MB } from '../constants';
import { trackEvent } from '../services/analytics';

interface UploadZoneProps {
  images: UploadedImage[];
  setImages: React.Dispatch<React.SetStateAction<UploadedImage[]>>;
  setError: (error: string | null) => void;
}

function loadImageDimensions(file: File): Promise<{ width: number; height: number; previewUrl: string }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight, previewUrl: url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`Failed to load ${file.name}`)); };
    img.src = url;
  });
}

const UploadZone: React.FC<UploadZoneProps> = ({ images, setImages, setError }) => {
  const [dragging, setDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback(async (files: FileList | File[]) => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of Array.from(files)) {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        errors.push(`${file.name}: unsupported format`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        errors.push(`${file.name}: exceeds ${MAX_FILE_SIZE_MB}MB`);
        continue;
      }
      validFiles.push(file);
    }

    if (errors.length > 0) {
      setError(errors.join('. '));
    }

    if (validFiles.length === 0) return;

    if (images.length === 0) {
      trackEvent('batch_upload_started');
    }

    setUploadProgress({ done: 0, total: validFiles.length });

    const newImages: UploadedImage[] = [];
    for (const file of validFiles) {
      try {
        const { width, height, previewUrl } = await loadImageDimensions(file);
        newImages.push({
          id: crypto.randomUUID(),
          file,
          previewUrl,
          originalWidth: width,
          originalHeight: height,
        });
      } catch {
        errors.push(`${file.name}: failed to read`);
      }
      setUploadProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
    }

    setImages((prev) => [...prev, ...newImages]);
    setUploadProgress(null);
  }, [images.length, setImages, setError]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }, [processFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = '';
    }
  }, [processFiles]);

  const removeImage = useCallback((id: string) => {
    setImages((prev) => {
      const removed = prev.find((img) => img.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((img) => img.id !== id);
    });
  }, [setImages]);

  const clearAll = useCallback(() => {
    images.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    setImages([]);
  }, [images, setImages]);

  const isUploading = uploadProgress !== null;
  const uploadPercent = isUploading && uploadProgress.total > 0
    ? Math.round((uploadProgress.done / uploadProgress.total) * 100)
    : 0;

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => { if (!isUploading) inputRef.current?.click(); }}
        className={`relative border-2 border-dashed rounded-2xl p-8 sm:p-12 text-center transition-all group overflow-hidden ${
          isUploading ? 'cursor-default' : 'cursor-pointer'
        } ${
          dragging
            ? 'border-cyan-500 bg-cyan-500/10'
            : isUploading
              ? 'border-cyan-500/60 bg-cyan-500/5'
              : 'border-slate-700 hover:border-cyan-500/50 hover:bg-slate-900/50'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_IMAGE_TYPES.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />
        {isUploading ? (
          <div className="flex flex-col items-center gap-4">
            <div className="relative w-14 h-14 flex items-center justify-center">
              <span className="absolute inset-0 rounded-xl bg-cyan-500/20 animate-ping" />
              <div className="relative w-14 h-14 rounded-xl bg-cyan-900/30 border border-cyan-500/30 flex items-center justify-center">
                <Loader2 size={24} className="text-cyan-400 animate-spin" />
              </div>
            </div>
            <div className="w-full max-w-xs">
              <p className="text-sm font-medium text-slate-200">
                Uploading {uploadProgress.done} of {uploadProgress.total} image{uploadProgress.total !== 1 ? 's' : ''}…
              </p>
              <div className="mt-3 h-1.5 w-full rounded-full bg-slate-800 overflow-hidden">
                <div
                  className="h-full rounded-full bg-cyan-500 transition-all duration-300 ease-out"
                  style={{ width: `${uploadPercent}%` }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center group-hover:bg-cyan-900/30 group-hover:border-cyan-500/30 transition-colors">
              <Upload size={24} className="text-slate-400 group-hover:text-cyan-400 transition-colors" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-200">
                Drag and drop images here, or <span className="text-cyan-400">browse</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                JPG, JPEG, PNG, WEBP &middot; Up to {MAX_FILE_SIZE_MB}MB each
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Thumbnail Grid */}
      {images.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-300 font-medium">
              {images.length} image{images.length !== 1 ? 's' : ''} selected
            </p>
            <button
              onClick={clearAll}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
            >
              <Trash2 size={14} />
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {images.map((image) => (
              <div key={image.id} className="relative group aspect-[4/3] rounded-lg overflow-hidden bg-slate-800 border border-slate-700">
                <img
                  src={image.previewUrl}
                  alt={image.file.name}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={(e) => { e.stopPropagation(); removeImage(image.id); }}
                  className="absolute top-1 right-1 w-6 h-6 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                >
                  <X size={12} className="text-white" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
                  <p className="text-[10px] text-slate-300 truncate">{image.file.name}</p>
                </div>
              </div>
            ))}

            {/* Add More Button */}
            <button
              onClick={() => inputRef.current?.click()}
              className="aspect-[4/3] rounded-lg border-2 border-dashed border-slate-700 hover:border-cyan-500/50 flex flex-col items-center justify-center gap-1 text-slate-500 hover:text-cyan-400 transition-all"
            >
              <ImageIcon size={20} />
              <span className="text-[10px]">Add more</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadZone;
